import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const parseSicils = (t) => [...new Set(t.split(/[^0-9]+/).filter((x) => x.length >= 4))];

export const BulkStatusDialog = ({ open, onOpenChange, onDone }) => {
  const [text, setText] = useState("");
  const [status, setStatus] = useState("faal");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);

  const list = parseSicils(text);

  const run = async (preview) => {
    setBusy(true);
    try {
      const { data } = await api.post("/voters/bulk-status", { sicil_nos: list, status, preview });
      setInfo(data);
      if (!preview) {
        toast.success(`${data.updated} firma ${status === "faal" ? "FAAL" : "ASKI"} yapıldı`);
        onDone?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setInfo(null); } onOpenChange(o); }}>
      <DialogContent className="max-w-xl" data-testid="bulk-status-dialog">
        <DialogHeader>
          <DialogTitle>Sicil No ile Toplu Durum Güncelle</DialogTitle>
          <DialogDescription>
            Sicil numaralarını virgül veya satır ile ayırarak yapıştırın. Yalnızca firma durumu
            değişir; tahmin, sorumlu, yetki belgesi ve notlar korunur.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Sicil numaraları ({list.length} numara)</Label>
            <Textarea rows={5} value={text} onChange={(e) => { setText(e.target.value); setInfo(null); }}
              placeholder="120493, 118376, 102809 ..." data-testid="bulk-status-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Yeni durum</Label>
            <Select value={status} onValueChange={(v) => { setStatus(v); setInfo(null); }}>
              <SelectTrigger className="h-9 w-48" data-testid="bulk-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="faal">FAAL yap</SelectItem>
                <SelectItem value="aski">ASKI yap</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {info && (
            <div className="rounded-lg border border-border p-3 text-sm space-y-1" data-testid="bulk-status-summary">
              <p><b>{info.found}</b> / {info.requested} sicil bulundu · değişecek: <b className="text-emerald-600">{info.will_change}</b> · zaten uygun: {info.already}</p>
              {info.missing?.length > 0 && (
                <p className="text-amber-600" data-testid="bulk-status-missing">
                  Bulunamayan: {info.missing.join(", ")}
                </p>
              )}
              {info.rows?.length > 0 && (
                <div className="max-h-40 overflow-auto text-xs text-muted-foreground pt-1">
                  {info.rows.map((r) => (
                    <div key={r.sicil_no}>{r.sicil_no} · {r.company_name} · #{r.committee_no} · {r.status === "faal" ? "FAAL" : "ASKI"}</div>
                  ))}
                </div>
              )}
              {info.updated > 0 && <p className="text-emerald-600 font-medium">{info.updated} firma güncellendi.</p>}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-status-cancel">Kapat</Button>
          <Button variant="secondary" disabled={busy || list.length === 0} onClick={() => run(true)} data-testid="bulk-status-check">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kontrol Et"}
          </Button>
          <Button className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={busy || !info || !info.will_change}
            onClick={() => run(false)} data-testid="bulk-status-apply">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Uygula (${info?.will_change ?? 0})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkStatusDialog;
